import { Edit2, Archive, Trash2, Copy, Package, MoreHorizontal, LinkIcon, ClipboardCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  useCreditPackages, 
  useArchiveCreditPackage, 
  useDeleteCreditPackage,
  useCreditPackageCalculation 
} from '@/hooks/billing';
import { CreditPackage, TranslationParams } from '@/types/billing';
import { useTranslation } from 'react-i18next';
import { useProviderCheckoutDomainSettings } from '@/hooks/provider/useProviderCheckoutDomainSettings';
import { resolveCheckoutOrigin } from '@/lib/checkout-origin';

interface CreditPackagesListProps {
  productId: string;
  onEditPackage: (_creditPackage: CreditPackage) => void;
  defaultCurrency?: string;
  planType?: 'individual' | 'teams';
  currentLanguage?: string;
  showTranslations?: boolean;
}

export function CreditPackagesList({ 
  productId, 
  onEditPackage,
  defaultCurrency = 'USD',
  planType,
  currentLanguage = 'en',
  showTranslations = false
}: CreditPackagesListProps) {
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { data: checkoutDomainSettings } = useProviderCheckoutDomainSettings();
  const checkoutOrigin = useMemo(
    () =>
      resolveCheckoutOrigin(
        checkoutDomainSettings?.checkoutDomain,
        checkoutDomainSettings?.checkoutDomainEnabled ?? false,
      ),
    [checkoutDomainSettings?.checkoutDomain, checkoutDomainSettings?.checkoutDomainEnabled],
  );
  // Create filters object
  const filters = planType ? { planType } : {};
  
  // Build translation parameters
  const translationParams: TranslationParams = {
    lang: currentLanguage,
    includeTranslations: showTranslations ? true : currentLanguage !== 'en' ? [currentLanguage] : undefined
  };
  
  // Fetch credit packages with translation support
  const { 
    data: allPackages = [], 
    isLoading, 
    error 
  } = useCreditPackages(
    productId,
    filters,
    { field: 'credits' },
    translationParams
  );

  // Client-side filter by plan type if backend filtering is not working
  const packages = planType 
    ? allPackages.filter(pkg => {
        console.log('Filtering package:', pkg.name, 'plan_type:', pkg.plan_type, 'looking for:', planType);
        return pkg.plan_type === planType;
      })
    : allPackages;
    
  console.log('CreditPackagesList - planType:', planType, 'allPackages count:', allPackages.length, 'filtered packages count:', packages.length);

  // Archive and delete mutations
  const archiveMutation = useArchiveCreditPackage();
  const deleteMutation = useDeleteCreditPackage();

  // Calculate savings and best value
  const { bestValue, calculateSavings } = useCreditPackageCalculation(packages, defaultCurrency);

  const handleArchivePackage = (pkg: CreditPackage) => {
    if (confirm(t('products.creditPackageList.confirmArchive', { name: pkg.name }))) {
      archiveMutation.mutate({ productId, packageId: pkg.id });
    }
  };

  const handleDeletePackage = (pkg: CreditPackage) => {
    if (confirm(t('products.creditPackageList.confirmDelete', { name: pkg.name }))) {
      deleteMutation.mutate({ productId, packageId: pkg.id });
    }
  };

  const handleDuplicatePackage = (pkg: CreditPackage) => {
    const duplicatedPackage = {
      ...pkg,
      id: '',
      name: t('products.creditPackageList.copyName', { name: pkg.name }),
      status: 'draft' as const
    };
    onEditPackage(duplicatedPackage);
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'active':
        return 'default';
      case 'draft':
        return 'secondary';
      case 'archived':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatPrice = (price: number, currency: string, options?: Intl.NumberFormatOptions): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(price);
  };

  const formatPricePerThousandCredits = (pkg: CreditPackage): string => {
    const rawPrice = pkg.prices[defaultCurrency];
    if (typeof rawPrice !== 'number' || pkg.credits <= 0) {
      return '—';
    }

    const pricePerThousand = (rawPrice / pkg.credits) * 1000;
    const isSmallValue = Math.abs(pricePerThousand) < 1;

    return formatPrice(pricePerThousand, defaultCurrency, {
      minimumFractionDigits: isSmallValue ? 2 : 0,
      maximumFractionDigits: isSmallValue ? 4 : 2,
    });
  };

  const buildPricingOptions = (pkg: CreditPackage) => {
    const priceEntries: Array<{ currency: string; amount: number; priceId?: string }> = [];
    const pricesArray =
      (pkg as CreditPackage & { pricesArray?: Array<Record<string, unknown>> }).pricesArray ??
      (pkg as CreditPackage & { prices_array?: Array<Record<string, unknown>> }).prices_array ??
      [];
    const priceIdByCurrency = new Map<string, string>();

    if (Array.isArray(pricesArray)) {
      pricesArray.forEach((entry) => {
        const currency =
          typeof entry.currency === 'string'
            ? entry.currency
            : typeof (entry.attributes as Record<string, unknown> | undefined)?.currency === 'string'
              ? (entry.attributes as Record<string, unknown>).currency as string
              : undefined;
        const priceId =
          typeof entry.id === 'string'
            ? entry.id
            : typeof entry.priceId === 'string'
              ? entry.priceId
              : typeof entry.price_id === 'string'
                ? entry.price_id
                : undefined;
        if (currency && priceId) {
          priceIdByCurrency.set(currency, priceId);
        }
      });
    }

    const pricesObject = Array.isArray(pkg.prices) ? {} : (pkg.prices ?? {});
    Object.entries(pricesObject).forEach(([currency, amount]) => {
      if (typeof amount !== 'number') return;
      priceEntries.push({
        currency,
        amount,
        priceId: priceIdByCurrency.get(currency),
      });
    });

    if (!priceEntries.length && Array.isArray(pricesArray)) {
      pricesArray.forEach((entry) => {
        const currency =
          typeof entry.currency === 'string'
            ? entry.currency
            : typeof (entry.attributes as Record<string, unknown> | undefined)?.currency === 'string'
              ? (entry.attributes as Record<string, unknown>).currency as string
              : undefined;
        const amount =
          typeof entry.amount === 'number'
            ? entry.amount
            : typeof entry.basePrice === 'number'
              ? entry.basePrice
              : typeof entry.base_price === 'number'
                ? entry.base_price
                : typeof (entry.attributes as Record<string, unknown> | undefined)?.amount === 'number'
                  ? (entry.attributes as Record<string, unknown>).amount as number
                  : typeof (entry.attributes as Record<string, unknown> | undefined)?.base_price === 'number'
                    ? (entry.attributes as Record<string, unknown>).base_price as number
                    : undefined;
        const priceId =
          typeof entry.id === 'string'
            ? entry.id
            : typeof entry.priceId === 'string'
              ? entry.priceId
              : typeof entry.price_id === 'string'
                ? entry.price_id
                : undefined;
        if (!currency || typeof amount !== 'number') return;
        priceEntries.push({ currency, amount, priceId });
      });
    }

    return priceEntries;
  };

  const handleCopyLink = async (pkg: CreditPackage, pricing: { currency: string; priceId?: string }) => {
    if (!productId || !pkg.id) return;
    const url = new URL('/customer/register', checkoutOrigin);
    url.searchParams.set('productId', productId);
    url.searchParams.set('packageId', pkg.id);
    url.searchParams.set('currency', pricing.currency);
    if (pricing.priceId) {
      url.searchParams.set('priceId', pricing.priceId);
    }
    const key = `${pkg.id}-${pricing.currency}`;

    try {
      await navigator.clipboard.writeText(url.toString());
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error('Copy checkout link failed', error);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('products.creditPackageList.loadError')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">

      {/* Loading State */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-1/2 mb-4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : packages.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-muted p-3">
              <Package className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-semibold">{t('products.creditPackageList.emptyTitle')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('products.creditPackageList.emptyDescription')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((pkg) => {
            const savings = calculateSavings(pkg);
            const isBestValue = bestValue?.id === pkg.id;
            const statusLabel = t(`products.creditPackageList.status.${pkg.status}`, { defaultValue: pkg.status });
            const pricingOptions = buildPricingOptions(pkg);

            return (
              <Card key={pkg.id} className={isBestValue ? 'ring-2 ring-green-500' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{pkg.name}</CardTitle>
                      {pkg.description && (
                        <CardDescription>{pkg.description}</CardDescription>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onEditPackage(pkg)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicatePackage(pkg)}>
                          <Copy className="h-4 w-4 mr-2" />
                          {t('common.duplicate')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {pkg.status !== 'archived' && (
                          <DropdownMenuItem onClick={() => handleArchivePackage(pkg)}>
                            <Archive className="h-4 w-4 mr-2" />
                            {t('common.archive')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleDeletePackage(pkg)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Package Price */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{t('products.creditPackageList.packagePriceLabel')}</p>
                    <p className="text-lg font-semibold">
                      {formatPrice(pkg.prices[defaultCurrency] || 0, defaultCurrency)}
                    </p>
                  </div>

                  {/* Credits */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{t('products.creditPackageList.creditsIncludedLabel')}</p>
                    <p className="text-lg font-semibold">
                      {t('products.creditPackageList.creditsCount', {
                        count: pkg.credits,
                        formattedCount: pkg.credits.toLocaleString(),
                      })}
                    </p>
                  </div>

                  {/* Cost per 1,000 credits with savings indicator */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{t('products.creditPackageList.costPerCreditLabel')}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {formatPricePerThousandCredits(pkg)}
                      </p>
                      {savings && savings.percentage > 0 && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          {t('products.creditPackageList.saveBadge', { value: savings.percentage.toFixed(0) })}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {pricingOptions.length > 0 && (
                    <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
                      <div className="text-xs uppercase tracking-wide text-zinc-400">
                        {t('products.creditPackageList.share', 'Share checkout link')}
                      </div>
                      <div className="space-y-2">
                        {pricingOptions.map((pricing) => (
                          <div key={`${pkg.id}-${pricing.currency}`} className="flex items-center justify-between gap-2 text-xs text-zinc-300">
                            <div className="space-y-1">
                              <div className="font-medium">{pricing.currency}</div>
                              <div className="text-[11px] text-zinc-500">
                                {t('products.creditPackageList.priceLabel', 'Price')}: {formatPrice(pricing.amount, pricing.currency)}
                                {pricing.priceId ? (
                                  <>
                                    {' '}· {t('products.creditPackageList.priceIdLabel', 'Price ID')}: {pricing.priceId}
                                  </>
                                ) : null}
                              </div>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
                              onClick={() => handleCopyLink(pkg, pricing)}
                              title={t('products.creditPackageList.copyLink', 'Copy checkout link')}
                            >
                              {copiedKey === `${pkg.id}-${pricing.currency}` ? (
                                <ClipboardCheck className="h-4 w-4" />
                              ) : (
                                <LinkIcon className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="pt-2">
                    <Badge variant={getStatusBadgeVariant(pkg.status)}>
                      {statusLabel}
                    </Badge>
                    {isBestValue && (
                      <Badge variant="default" className="bg-green-500 ml-2">
                        {t('products.creditPackageList.bestValueBadge')}
                      </Badge>
                    )}
                  </div>

                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
